import React, { useState, useEffect } from "react";
import { Card, Button, Form, InputGroup, Modal } from "react-bootstrap";
import {
  IconSearch,
  IconDotsVertical,
  IconEye,
  IconFileTypePdf,
  IconChevronRight,
  IconExternalLink,
} from "@tabler/icons-react";
import moment from "moment";
import { toast } from "react-toastify";
import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";
import Loader from "../../components/Loader";
import useIdleLogout from "../../hooks/useIdleLogout";

const LIMIT = 20;

/* ───────────────────── Detail Modal ───────────────────── */
const PackSlipDetailModal = ({ slip, onHide }) => {
  if (!slip) return null;

  return (
    <Modal show={!!slip} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Pack Slip #{slip.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-6">
            <small className="text-muted">Order ID</small>
            <p className="fw-semibold mb-2">{slip.Order?.order_id || "N/A"}</p>
          </div>
          <div className="col-md-6">
            <small className="text-muted">Created By</small>
            <p className="fw-semibold mb-2">
              {slip.Order?.createdBy
                ? `${slip.Order.createdBy.first_name} ${slip.Order.createdBy.last_name}`
                : "N/A"}
            </p>
          </div>
          <div className="col-md-6">
            <small className="text-muted">From Address</small>
            <p className="mb-2">{slip.from_address || "N/A"}</p>
          </div>
          <div className="col-md-6">
            <small className="text-muted">To Address</small>
            <p className="mb-2">{slip.to_address || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Ship Date</small>
            <p className="mb-2">
              {slip.ship_date
                ? moment(slip.ship_date).format("MM/DD/YYYY")
                : "N/A"}
            </p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Contract #</small>
            <p className="mb-2">{slip.contract_number || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Invoice #</small>
            <p className="mb-2">{slip.invoice_number || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Shipping Method</small>
            <p className="mb-2">
              {slip.shipping_method || "N/A"}
              {slip.shipping_method_detail
                ? ` (${slip.shipping_method_detail})`
                : ""}
            </p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Tracking #</small>
            <p className="mb-2">{slip.tracking_number || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Total Boxes</small>
            <p className="mb-2">{slip.total_boxes || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Carrier</small>
            <p className="mb-2">{slip.carrier || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Purchase Order</small>
            <p className="mb-2">{slip.purchase_order || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Shipping Cost</small>
            <p className="mb-2">
              {slip.shipping_cost != null ? `$${slip.shipping_cost}` : "N/A"}
            </p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Amendment</small>
            <p className="mb-2">{slip.amendment || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Delivery Order</small>
            <p className="mb-2">{slip.delivery_order || "N/A"}</p>
          </div>
          <div className="col-md-4">
            <small className="text-muted">Deliver to Customer</small>
            <p className="mb-2">{slip.deliver_to_customer ? "Yes" : "No"}</p>
          </div>
          <div className="col-md-6">
            <small className="text-muted">Sales Lead</small>
            <p className="mb-2">
              {slip.sales_lead_name || "N/A"}
              {slip.sales_lead_email ? ` (${slip.sales_lead_email})` : ""}
            </p>
          </div>
          <div className="col-md-6">
            <small className="text-muted">Notification Emails</small>
            <p className="mb-2">{slip.notification_emails || "N/A"}</p>
          </div>
          {slip.notes && (
            <div className="col-12">
              <small className="text-muted">Notes</small>
              <p className="mb-2">{slip.notes}</p>
            </div>
          )}
        </div>

        {slip.products?.length > 0 && (
          <>
            <hr />
            <h6 className="mb-2">Products ({slip.products.length})</h6>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>UOM</th>
                    <th>Customer Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {slip.products.map((p, i) => (
                    <tr key={p.id || i}>
                      <td>{p.sku_display_name || p.sku_id || "N/A"}</td>
                      <td>{p.name || "N/A"}</td>
                      <td>{p.description || "N/A"}</td>
                      <td>{p.quantity}</td>
                      <td>{p.unit_of_measure || "N/A"}</td>
                      <td>
                        ${parseFloat(p.customer_price || 0).toFixed(2)}
                      </td>
                      <td>
                        ${parseFloat(p.total_price || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/* ───────────────────── Listing Page ───────────────────── */
const PackSlipListing = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedSlip, setSelectedSlip] = useState(null);

  useIdleLogout();

  const { postData: fetchPackSlips } = usePostRequest(
    API_ENDPOINTS.GET_ALL_PACKAGING_SLIPS
  );

  /* ── fetch ── */
  const fetchData = (pg, srch) => {
    setLoading(true);
    fetchPackSlips(
      { page: pg, limit: LIMIT, search: srch },
      (res) => {
        const rows = res.data || [];
        setData((prev) => (pg === 1 ? rows : [...prev, ...rows]));
        setPagination(res.pagination || { total: 0, totalPages: 1 });
        setLoading(false);
      },
      () => {
        toast.error("Failed to fetch pack slips");
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchData(1, "");
  }, []);

  // close dropdown on outside click
  useEffect(() => {
    const handler = () => setOpenDropdown(null);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  /* ── handlers ── */
  const handleSearch = () => {
    const val = searchInput.trim();
    setSearch(val);
    setPage(1);
    fetchData(1, val);
  };

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchData(next, search);
  };

  const handleDownloadPDF = (slip) => {
    // TODO: plug in your PackSlip PDF component / endpoint
    toast.info("PDF download will be available soon");
  };

  /* ── render ── */
  return (
    <>
      {loading && data.length === 0 && <Loader />}

      {/* Header */}
      <div className="page-header d-print-none mb-3">
        <h2 className="page-title">Pack Slips</h2>
      </div>

      {/* Search bar */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="p-3">
          <Form
            className="d-flex align-items-center gap-3"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          >
            <InputGroup style={{ maxWidth: 400 }}>
              <Form.Control
                className="bg-light shadow-none"
                placeholder="Search by Order ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                autoComplete="off"
              />
              <InputGroup.Text className="bg-white border-start-0">
                <IconSearch stroke={2} />
              </InputGroup.Text>
            </InputGroup>

            <Button variant="warning" onClick={handleSearch}>
              <IconSearch size={16} /> Search
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Count */}
      <div
        className="d-flex align-items-center mb-2 px-1 text-muted fw-semibold"
        style={{ fontSize: 14 }}
      >
        SHOWING {data.length} OF {pagination.total} PACK SLIPS
      </div>

      {/* Table */}
      <Card className="shadow-sm">
        <div className="table-responsive">
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Pack Slip #</th>
                <th>From Address</th>
                <th>To Address</th>
                <th>Ship Date</th>
                <th>Contract #</th>
                <th>Tracking #</th>
                <th>Total Boxes</th>
                <th>Date Generated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((row, idx) => {
                  const rowId = `slip-${row.id}-${idx}`;
                  return (
                    <tr key={rowId} className="align-middle">
                      <td>
                        <a
                          href={`/edit-order/${row.Order?.order_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary fw-semibold"
                          style={{ textDecoration: "none" }}
                        >
                          {row.Order?.order_id || "N/A"}
                        </a>
                      </td>
                      <td>{row.id}</td>
                      <td style={{ maxWidth: 200, whiteSpace: "normal" }}>
                        {row.from_address || "N/A"}
                      </td>
                      <td style={{ maxWidth: 200, whiteSpace: "normal" }}>
                        {row.to_address || "N/A"}
                      </td>
                      <td>
                        {row.ship_date
                          ? moment(row.ship_date).format("MM/DD/YYYY")
                          : "N/A"}
                      </td>
                      <td>{row.contract_number || "N/A"}</td>
                      <td>{row.tracking_number || "N/A"}</td>
                      <td>{row.total_boxes || "N/A"}</td>
                      <td>
                        {row.createdAt
                          ? moment(row.createdAt).format("MM/DD/YYYY")
                          : "N/A"}
                      </td>

                      {/* Action dropdown */}
                      <td style={{ position: "relative" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(
                              openDropdown === rowId ? null : rowId
                            );
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          <IconDotsVertical size={20} />
                        </button>

                        {openDropdown === rowId && (
                          <ul className="order-dropdown-menu p-0">
                            <li
                              className="dropdown-item"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setOpenDropdown(null);
                                window.open(
                                  `/edit-order/${row.Order?.order_id}`,
                                  "_blank"
                                );
                              }}
                            >
                              <span>
                                <IconExternalLink stroke={1} /> Order Details
                              </span>
                              <IconChevronRight stroke={1} />
                            </li>
                            <li
                              className="dropdown-item border-top"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setOpenDropdown(null);
                                setSelectedSlip(row);
                              }}
                            >
                              <span>
                                <IconEye stroke={1} /> Packslip Details
                              </span>
                              <IconChevronRight stroke={1} />
                            </li>
                            <li
                              className="dropdown-item border-top"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setOpenDropdown(null);
                                handleDownloadPDF(row);
                              }}
                            >
                              <span>
                                <IconFileTypePdf stroke={1} /> Download PDF
                              </span>
                              <IconChevronRight stroke={1} />
                            </li>
                          </ul>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="text-center p-4">
                    {loading ? "Loading..." : "No pack slips found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Load More */}
        {page < pagination.totalPages && data.length > 0 && (
          <div className="text-center p-3">
            <Button
              variant="outline-primary"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? "Loading..." : "Load More"}
            </Button>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <PackSlipDetailModal
        slip={selectedSlip}
        onHide={() => setSelectedSlip(null)}
      />
    </>
  );
};

export default PackSlipListing;
